import React from "react";
import {
  Autocomplete,
  Box,
  Paper,
  Grid,
  Stepper,
  Step,
  StepLabel,
  TextField,
  Typography,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Chip,
  Stack,
  Alert,
  CircularProgress,
  Divider,
} from "@mui/material";
import LockResetIcon from "@mui/icons-material/LockReset";
import LockOpenIcon from "@mui/icons-material/LockOpen";
import BlockIcon from "@mui/icons-material/Block";
import GroupRemoveIcon from "@mui/icons-material/GroupRemove";
import GroupsIcon from "@mui/icons-material/Groups";
import GroupAddIcon from "@mui/icons-material/GroupAdd";
import PersonSearchIcon from "@mui/icons-material/PersonSearch";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";
import CryptoJS from "crypto-js";
import { ContextSession } from "../../services/ContextSession";
import { useNavigate } from "react-router-dom";

const URL_BEI360 = "https://bei360.injupemp.gob.hn:8444/";
const SECRET = "2BjBrJWbyVCXVF)5DPd_?)&lln~l!i&85An2=kac3_&£dKSuR_";

const ACTIONS = [
  { id: "UNLOCK", label: "Desbloqueo", icon: <LockOpenIcon fontSize="small" /> },
  { id: "RESET", label: "Reseteo de clave", icon: <LockResetIcon fontSize="small" /> },
  { id: "DISABLE", label: "Deshabilitacion de usuario", icon: <BlockIcon fontSize="small" /> },
  { id: "REMOVE_GROUP", label: "Eliminacion de un grupo", icon: <GroupRemoveIcon fontSize="small" /> },
  { id: "LIST_GROUPS", label: "Listar grupos", icon: <GroupsIcon fontSize="small" /> },
  { id: "ADD_GROUP", label: "Agregar grupos", icon: <GroupAddIcon fontSize="small" /> },
];

const tipoByAction = (action) => {
  switch (action) {
    case "RESET":
      return 1;
    case "UNLOCK":
      return 2;
    case "DISABLE":
      return 5;
    case "LIST_GROUPS":
      return 4;
    case "REMOVE_GROUP":
      return 6;
    case "ADD_GROUP":
      return 7;
    default:
      return 1;
  }
};

const buildRequestError = (response, data, fallbackMessage) => {
  const error = new Error(
    data?.message || data?.error || fallbackMessage || "Error en la operacion.",
  );
  error.status = response.status;
  error.code = data?.code;
  error.payload = data;
  return error;
};

const enviarAccion = async ({ username, action, group, accessToken }) => {
  const user = (username || "").trim();
  if (!user) throw new Error("Username requerido");

  const tipo = tipoByAction(action);
  const queryString = `username=${encodeURIComponent(user)}`;
  const signature = CryptoJS.HmacSHA256(queryString, SECRET).toString(
    CryptoJS.enc.Hex
  );

  const formData = new FormData();
  formData.append("username", user);
  formData.append("tipo", String(tipo));
  if ((tipo === 3 || tipo === 6 || tipo === 7) && group) {
    formData.append("group", group);
  }

  const res = await fetch(`${URL_BEI360}api/reset/users/`, {
    method: "POST",
    body: formData,
    headers: {
      "x-signature": signature,
      Accept: "application/json",
      ...(accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
    },
  });

  const data = await res.json().catch(() => ({}));
  if (!res.ok) {
    throw buildRequestError(res, data, "Error en la operacion.");
  }
  return data;
};

export default function Dominios() {
  const sessi = React.useContext(ContextSession);
  const navigate = useNavigate();
  const [activeStep, setActiveStep] = React.useState(0);
  const [username, setUsername] = React.useState("");
  const [searchText, setSearchText] = React.useState("");
  const [employeeOptions, setEmployeeOptions] = React.useState([]);
  const [employeeLoading, setEmployeeLoading] = React.useState(false);
  const [selectedEmployee, setSelectedEmployee] = React.useState(null);
  const [selectedAction, setSelectedAction] = React.useState("");
  const [groupName, setGroupName] = React.useState("");
  const [groupList, setGroupList] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [errorMsg, setErrorMsg] = React.useState("");
  const [successMsg, setSuccessMsg] = React.useState("");

  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down("sm"));

  const isUserStepValid = React.useMemo(
    () => username.trim().length >= 3,
    [username]
  );
  const isActionStepValid = React.useMemo(() => {
    if (!selectedAction) return false;
    if (selectedAction === "ADD_GROUP" || selectedAction === "REMOVE_GROUP") {
      return groupName.trim().length >= 2;
    }
    return true;
  }, [selectedAction, groupName]);

  const steps = ["Usuario", "Accion", "Resultado"];

  React.useEffect(() => {
    const term = searchText.trim();
    const selectedUser = selectedEmployee?.Usuario?.trim() || "";

    if (term.length < 2) {
      setEmployeeOptions([]);
      setEmployeeLoading(false);
      return undefined;
    }

    if (selectedUser && term.toUpperCase() === selectedUser.toUpperCase()) {
      setEmployeeLoading(false);
      return undefined;
    }

    let active = true;
    setEmployeeLoading(true);

    const timeoutId = window.setTimeout(async () => {
      try {
        const params = new URLSearchParams({ search: term });
        const res = await fetch(
          `${URL_BEI360}api/blacklist/users-siac/?${params.toString()}`,
          {
            method: "GET",
            headers: {
              Accept: "application/json",
              ...(sessi?.getToken
                ? { Authorization: `Bearer ${sessi.getToken()}` }
                : {}),
            },
          }
        );
        const data = await res.json().catch(() => ({}));

        if (!res.ok) {
          throw buildRequestError(
            res,
            data,
            "No se pudo buscar el empleado."
          );
        }

        if (active) {
          setEmployeeOptions(Array.isArray(data) ? data : []);
        }
      } catch (error) {
        if (error?.status === 401 && error?.code === "token_not_valid") {
          sessi.clearSession();
          navigate("/");
          return;
        }
        if (active) {
          setEmployeeOptions([]);
          setErrorMsg(error.message || "No se pudo buscar el empleado.");
        }
      } finally {
        if (active) {
          setEmployeeLoading(false);
        }
      }
    }, 350);

    return () => {
      active = false;
      window.clearTimeout(timeoutId);
    };
  }, [navigate, searchText, selectedEmployee, sessi]);

  const handleDomainUserChange = (event, newValue) => {
    setErrorMsg("");
    setSuccessMsg("");

    if (!newValue) {
      setSelectedEmployee(null);
      setUsername("");
      setSearchText("");
      return;
    }

    if (typeof newValue === "string") {
      const typedUser = newValue.trim();
      setSelectedEmployee(null);
      setUsername(typedUser);
      setSearchText(typedUser);
      return;
    }

    const selectedUser = newValue?.Usuario?.trim() || "";
    setSelectedEmployee(newValue);
    setUsername(selectedUser);
    setSearchText(selectedUser);
  };

  const handleDomainUserInputChange = (event, newInputValue, reason) => {
    if (reason === "reset") return;

    setSearchText(newInputValue);

    if (reason === "input" || reason === "clear") {
      setSelectedEmployee(null);
      setUsername(newInputValue.trim());
    }
  };

  const handleNext = async () => {
    setErrorMsg("");
    setSuccessMsg("");

    if (activeStep === 1) {
      setLoading(true);
      try {
        const accessToken = sessi?.getToken ? sessi.getToken() : "";
        const data = await enviarAccion({
          username,
          action: selectedAction,
          group: groupName,
          accessToken,
        });
        const rawGroups =
          data?.Resultado?.groups || data?.groups || [];
        const groupNames = rawGroups
          .map((item) => {
            if (typeof item === "string") return item;
            return item?.cn || item?.name || "";
          })
          .filter(Boolean);

        if (selectedAction === "LIST_GROUPS") {
          setGroupList(groupNames);
          setSuccessMsg(
            groupNames.length
              ? `Se encontraron ${groupNames.length} grupos.`
              : "No se encontraron grupos."
          );
        } else {
          setGroupList([]);
          setSuccessMsg(data?.message || "Accion enviada correctamente.");
        }
        setActiveStep((s) => s + 1);
      } catch (e) {
        if (e?.status === 401 && e?.code === "token_not_valid") {
          sessi.clearSession();
          navigate("/");
          return;
        }
        setErrorMsg(e.message);
      } finally {
        setLoading(false);
      }
      return;
    }

    setActiveStep((s) => s + 1);
  };

  const handleBack = () => {
    setErrorMsg("");
    setSuccessMsg("");
    setActiveStep((s) => Math.max(0, s - 1));
  };

  const resetAll = () => {
    setActiveStep(0);
    setUsername("");
    setSearchText("");
    setEmployeeOptions([]);
    setEmployeeLoading(false);
    setSelectedEmployee(null);
    setSelectedAction("");
    setGroupName("");
    setGroupList([]);
    setErrorMsg("");
    setSuccessMsg("");
  };

  const renderStepContent = () => {
    switch (activeStep) {
      case 0:
        return (
          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Usuario de dominio
            </Typography>
            <Autocomplete
              freeSolo
              filterOptions={(options) => options}
              options={employeeOptions}
              value={selectedEmployee}
              inputValue={searchText}
              loading={employeeLoading}
              onChange={handleDomainUserChange}
              onInputChange={handleDomainUserInputChange}
              getOptionLabel={(option) =>
                typeof option === "string" ? option : option?.Usuario || ""
              }
              isOptionEqualToValue={(option, value) =>
                option?.UserID === value?.UserID
              }
              noOptionsText={
                searchText.trim().length < 2
                  ? "Escribe al menos 2 caracteres"
                  : "Sin resultados"
              }
              renderOption={(props, option) => (
                <Box component="li" {...props}>
                  <Stack spacing={0.25}>
                    <Typography fontWeight={700}>
                      {option?.Usuario || "N/A"}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {option?.NombreCompleto || "Sin nombre registrado"}
                    </Typography>
                  </Stack>
                </Box>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  size={isXs ? "small" : "medium"}
                  label="Usuario"
                  placeholder="Buscar por nombre o usuario"
                  fullWidth
                  required
                  helperText="Usuario de dominio a gestionar."
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <>
                        <PersonSearchIcon
                          sx={{ mr: 1, color: "text.secondary" }}
                        />
                        {params.InputProps.startAdornment}
                      </>
                    ),
                    endAdornment: (
                      <>
                        {employeeLoading ? (
                          <CircularProgress color="inherit" size={20} />
                        ) : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          </Box>
        );
      case 1:
        return (
          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Selecciona la accion
            </Typography>
            <RadioGroup
              value={selectedAction}
              onChange={(e) => setSelectedAction(e.target.value)}
              sx={{ mt: 1 }}
            >
              <Grid container spacing={1}>
                {ACTIONS.map((opt) => (
                  <Grid size={{ xs: 12, sm: 6, md: 4 }} key={opt.id}>
                    <Paper
                      variant="outlined"
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        ...(selectedAction === opt.id && {
                          borderColor: "primary.main",
                          bgcolor: "rgba(14,165,233,0.06)",
                        }),
                      }}
                    >
                      <FormControlLabel
                        value={opt.id}
                        control={<Radio />}
                        label={
                          <Stack direction="row" spacing={1} alignItems="center">
                            {opt.icon}
                            <Typography>{opt.label}</Typography>
                          </Stack>
                        }
                      />
                    </Paper>
                  </Grid>
                ))}
              </Grid>
            </RadioGroup>

            {(selectedAction === "ADD_GROUP" ||
              selectedAction === "REMOVE_GROUP") && (
              <>
                <Divider sx={{ my: 2 }} />
                <TextField
                  size={isXs ? "small" : "medium"}
                  label="Grupo"
                  fullWidth
                  required
                  value={groupName}
                  onChange={(e) => setGroupName(e.target.value)}
                />
              </>
            )}
          </Box>
        );
      case 2:
        return (
          <Box textAlign="center">
            <Typography variant="h5" fontWeight={800} sx={{ mt: 1 }}>
              Proceso completado
            </Typography>
            <Typography sx={{ mt: 1 }}>
              {successMsg || "Accion realizada correctamente."}
            </Typography>
            {selectedAction === "LIST_GROUPS" && groupList.length > 0 && (
              <Stack
                direction="row"
                spacing={1}
                justifyContent="center"
                flexWrap="wrap"
                sx={{ mt: 2 }}
              >
                {groupList.map((group) => (
                  <Chip key={group} label={group} />
                ))}
              </Stack>
            )}
            <Button sx={{ mt: 2 }} variant="contained" onClick={resetAll}>
              Realizar otra gestion
            </Button>
          </Box>
        );
      default:
        return null;
    }
  };

  const canNext =
    (activeStep === 0 && isUserStepValid) ||
    (activeStep === 1 && isActionStepValid) ||
    activeStep >= 2;

  return (
    <Grid container justifyContent="center"    alignItems="flex-start" sx={{ py: 5 }}>
      <Grid size={{ xs: 8 }}>
        <Paper
        
          elevation={3}
          sx={{
            p: { xs: 8, md: 8 },
            borderRadius: 4,
            mx: "auto",
            width: "100%",
            maxWidth: "100%",
          }}
        >
          <Stack spacing={2}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography variant="h4" fontWeight={900}>
                Dominios
              </Typography>
              <Chip label="Soporte" size="small" />
            </Stack>

            <Stepper activeStep={activeStep} sx={{ pt: 1, pb: 3 }}>
              {steps.map((label) => (
                <Step key={label}>
                  <StepLabel>{label}</StepLabel>
                </Step>
              ))}
            </Stepper>

            {errorMsg && <Alert severity="error">{errorMsg}</Alert>}
            {successMsg && activeStep !== 3 && (
              <Alert severity="success">{successMsg}</Alert>
            )}

            <Box>{renderStepContent()}</Box>

            <Stack direction="row" justifyContent="space-between" mt={2}>
              <Button disabled={activeStep === 0 || loading} onClick={handleBack}>
                Atras
              </Button>
              <Button
                variant="contained"
                disabled={!canNext || loading || activeStep === 2}
                onClick={handleNext}
              >
                {loading ? (
                  <CircularProgress size={22} sx={{ color: "white" }} />
                ) : activeStep >= 1 ? (
                  activeStep === 1 ? (
                    "Confirmar y ejecutar"
                  ) : (
                    "Finalizado"
                  )
                ) : (
                  "Continuar"
                )}
              </Button>
            </Stack>
          </Stack>
        </Paper>
      </Grid>
    </Grid>
  );
}
